import React, { ReactNode, useCallback, useEffect, useRef } from "react";
import { useInView } from "../../hooks/useInView";

interface InViewWithCallbackProps {
  /** 子组件 */
  children:
    | ReactNode
    | ((inView: boolean, entry?: IntersectionObserverEntry) => ReactNode);
  /** 触发的阈值，0-1之间 */
  threshold?: number | number[];
  /** 根元素的外边距 */
  rootMargin?: string;
  /** 指定根元素，默认为浏览器视口 */
  root?: Element | null;
  /** 是否只触发一次 */
  triggerOnce?: boolean;
  /** 进入视图时的回调 */
  onInView?: (entry: IntersectionObserverEntry) => void;
  /** 离开视图时的回调 */
  onOutView?: (entry: IntersectionObserverEntry) => void;
  /** 状态变化时的回调 - 向父组件传递状态 */
  onVisibilityChange?: (
    inView: boolean,
    entry: IntersectionObserverEntry,
  ) => void;
  /** 实时状态变化回调 - 包含可见性比例 */
  onIntersectionChange?: (entry: IntersectionObserverEntry) => void;
  /** 容器的样式 */
  style?: React.CSSProperties;
  /** 容器的类名 */
  className?: string;
  /** 容器的HTML标签，默认为div */
  as?: keyof JSX.IntrinsicElements;
  /** 是否启用详细监听模式（监听可见比例变化） */
  enableDetailedTracking?: boolean;
}

/**
 * 增强版视图监听组件
 * 支持多种状态传递方式，向父组件实时传递视图状态
 */
const InViewWithCallback: React.FC<InViewWithCallbackProps> = ({
  children,
  threshold = 0,
  rootMargin = "0px",
  root = null,
  triggerOnce = false,
  onInView,
  onOutView,
  onVisibilityChange,
  onIntersectionChange,
  style,
  className,
  as: Component = "div",
  enableDetailedTracking = false,
}) => {
  const prevInView = useRef<boolean | null>(null);

  // 使用增强的配置
  const { ref, inView, entry } = useInView({
    threshold: enableDetailedTracking ? [0, 0.25, 0.5, 0.75, 1] : threshold,
    rootMargin,
    root,
    triggerOnce,
  });

  // 状态变化回调
  const handleVisibilityChange = useCallback(
    (currentInView: boolean, currentEntry: IntersectionObserverEntry) => {
      // 只在状态真正变化时触发回调
      if (prevInView.current !== currentInView) {
        onVisibilityChange?.(currentInView, currentEntry);

        // 触发进入/离开回调
        if (currentInView && onInView) {
          onInView(currentEntry);
        } else if (!currentInView && onOutView) {
          onOutView(currentEntry);
        }

        prevInView.current = currentInView;
      }
    },
    [onInView, onOutView, onVisibilityChange],
  );

  // 监听状态变化
  useEffect(() => {
    if (entry) {
      // 触发可见性变化回调
      handleVisibilityChange(inView, entry);

      // 触发交叉观察回调（实时监听）
      if (enableDetailedTracking && onIntersectionChange) {
        onIntersectionChange(entry);
      }
    }
  }, [
    inView,
    entry,
    handleVisibilityChange,
    onIntersectionChange,
    enableDetailedTracking,
  ]);

  return React.createElement(
    Component,
    { ref: ref as any, style, className },
    typeof children === "function" ? children(inView, entry) : children,
  );
};

export default InViewWithCallback;
